import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Phone, Mail, MapPin, Send, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { companyInfo, services } from '../mock';
import BackButton from '../components/BackButton';
import { trackContactFormSubmit, trackPhoneClick, trackEmailClick } from '../components/GoogleAnalytics';
import { trackGenerateLead } from '../utils/analytics';
import { executeReCaptcha } from '../utils/recaptcha';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import VilleAutocomplete from '../components/VilleAutocomplete';
import AntiSpamFields from '../components/AntiSpamFields';

const Contact = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'Contactez-nous - Devis Gratuit Béton Décoratif | DumaExpertBéton';
    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) descMeta.setAttribute('content', 'Contactez DumaExpertBéton pour un devis gratuit béton imprimé, désactivé ou balayé. Téléphone, email, formulaire. Expert PACA depuis 2014.');
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://duma-expert-beton.fr/contact';
    return () => { document.title = 'DumaExpertBéton - Expert en Béton Imprimé et Décoratif | Orange, Vaucluse'; };
  }, []);
  const navigate = useNavigate();
  
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    service: '',
    surface: '',
    ville: '',
    message: '',
    gdpr_consent: false,
    honeypot: '' // Honeypot field - bots will fill this
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const antiSpamRef = React.useRef(null);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check - if filled, it's a bot
    if (formData.honeypot) {
      console.log('Bot detected - honeypot filled');
      return; // Silently reject
    }
    
    setIsSubmitting(true);
    
    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeReCaptcha('contact_form');
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        ...formData,
        recaptchaToken,
        cf_turnstile_token: turnstileToken,
        website: antiSpamRef.current?.getHoneypotValue() || ''
      });
      
      if (response.data.success) {
        // Track conversion in Google Analytics
        trackContactFormSubmit(formData);
        trackGenerateLead('contact_submit');
        setIsSubmitted(true);
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            nom: '',
            prenom: '',
            email: '',
            telephone: '',
            service: '',
            surface: '',
            ville: '',
            message: '',
            gdpr_consent: false
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(t('contact.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <SEO 
        title={`${t('contact.title')} - DumaExpertBéton`}
        description={t('contact.subtitle')}
        url="https://dumaexpertbeton.fr/contact"
      />
      {/* Header */}
      <div className="bg-slate-900 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="flex justify-between items-start mb-4">
            <button onClick={handleBack} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700 transition-all duration-200 cursor-pointer">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">{t('common.back')}</span>
            </button>
            <LanguageSwitcher />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mt-4">{t('contact.title')}</h1>
          <p className="text-xl text-slate-300 mt-4">{t('contact.subtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-2xl text-white">{t('contact.formTitle')}</CardTitle>
                <CardDescription className="text-slate-300">
                  {t('contact.formDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-2">{t('contact.successTitle')}</h3>
                    <p className="text-slate-300">{t('contact.successMessage')}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="nom">{t('contact.lastName')}</Label>
                        <Input 
                          id="nom"
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          required
                          placeholder={t('contact.yourName')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="prenom">{t('contact.firstName')}</Label>
                        <Input 
                          id="prenom"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleChange}
                          required
                          placeholder={t('contact.yourFirstName')}
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">{t('contact.email')}</Label>
                        <Input 
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder={t('contact.emailPlaceholder')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="telephone">{t('contact.phone')}</Label>
                        <Input 
                          id="telephone"
                          name="telephone"
                          type="tel"
                          value={formData.telephone}
                          onChange={handleChange}
                          required
                          placeholder={t('contact.phonePlaceholder')}
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contact-service">{t('contact.concreteType')}</Label>
                        <Select value={formData.service} onValueChange={handleServiceChange} required>
                          <SelectTrigger id="contact-service">
                            <SelectValue placeholder={t('contact.selectService')} />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map((service) => (
                              <SelectItem key={service.id} value={service.slug}>
                                {service.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact-surface">{t('contact.estimatedSurface')}</Label>
                        <Input 
                          id="contact-surface"
                          name="surface"
                          type="number"
                          value={formData.surface}
                          onChange={handleChange}
                          placeholder={t('contact.surfacePlaceholder')}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ville">{t('contact.city')}</Label>
                      <VilleAutocomplete
                        id="ville"
                        name="ville"
                        value={formData.ville}
                        onChange={handleChange}
                        required
                        placeholder={t('contact.cityPlaceholder')}
                      />
                    </div>

                    {/* Honeypot field - hidden from users, bots will fill it */}
                    <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                      <input
                        type="text"
                        name="honeypot"
                        value={formData.honeypot}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">{t('contact.describeProject')}</Label>
                      <Textarea 
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder={t('contact.projectPlaceholder')}
                      />
                    </div>

                    {/* GDPR Consent Checkbox */}
                    <div className="flex items-start gap-3 p-4 bg-slate-700 rounded-lg border border-slate-600">
                      <input
                        type="checkbox"
                        id="gdpr_consent"
                        name="gdpr_consent"
                        checked={formData.gdpr_consent}
                        onChange={handleChange}
                        required
                        className="mt-1 h-4 w-4 text-[#C97249] focus:ring-[#C97249] border-slate-500 rounded cursor-pointer"
                      />
                      <label htmlFor="gdpr_consent" className="text-sm text-slate-200 cursor-pointer">
                        {t('contact.gdprConsent')}{' '}
                        <Link to="/confidentialite" target="_blank" className="text-white font-semibold underline hover:text-[#C97249]">
                          {t('contact.privacyPolicy')}
                        </Link>
                        . *
                      </label>
                    </div>

                    <AntiSpamFields ref={antiSpamRef} onTurnstileVerify={setTurnstileToken} />

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-[#C97249] hover:bg-[#B8624A] text-white"
                      disabled={isSubmitting || !formData.gdpr_consent}
                    >
                      {isSubmitting ? (
                        t('contact.sending')
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          {t('contact.send')}
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="bg-slate-900 text-white border-slate-800">
              <CardHeader>
                <CardTitle>{t('contact.contactInfo')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <a href={`tel:${companyInfo.phoneRaw}`} className="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors">
                  <Phone className="w-5 h-5 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t('contact.phoneLabel')}</p>
                    <p className="text-slate-300">{companyInfo.phone}</p>
                  </div>
                </a>
                <a href={`mailto:${companyInfo.email}`} className="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors">
                  <Mail className="w-5 h-5 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t('contact.emailLabel')}</p>
                    <p className="text-slate-300">{companyInfo.email}</p>
                  </div>
                </a>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50">
                  <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t('contact.location')}</p>
                    <p className="text-slate-300">{companyInfo.address}</p>
                    <p className="text-slate-400 text-sm mt-1">{companyInfo.region}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">{t('contact.serviceArea')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-slate-700 p-4 rounded-lg border border-slate-600">
                    <p className="font-bold text-white mb-2">
                      {t('contact.serviceAreaRadius')}
                    </p>
                    <p className="text-sm text-slate-300">
                      {t('contact.serviceAreaDescription')}
                    </p>
                  </div>
                  <ul className="space-y-2 text-slate-200">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      {t('contact.areaOrange')}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {t('contact.areaAvignon')}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {t('contact.areaCarpentras')}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {t('contact.areaChateauneuf')}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {t('contact.areaVaucluse')}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {t('contact.areaPACA')}
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
              <CardContent className="p-6">
                <h3 className="font-bold text-slate-900 mb-2">{t('contact.fastResponse')}</h3>
                <p className="text-slate-600 text-sm">
                  {t('contact.fastResponseMessage')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;